import api from '../axiosIntercepture';

/** Same list envelope parsing as stock-transfer-out (`{ data: { data, meta } }`). */
function normalizeListResponse(body) {
  if (!body) return { rows: [], total: 0 };
  const inner = body.data !== undefined ? body.data : body;

  if (Array.isArray(inner)) {
    return { rows: inner, total: body.total ?? body.count ?? inner.length };
  }
  const arrayKeys = ['data', 'results', 'items', 'records', 'list', 'rows', 'docs'];
  for (const key of arrayKeys) {
    if (Array.isArray(inner?.[key])) {
      return {
        rows: inner[key],
        total:
          inner.meta?.total ??
          inner.total ??
          inner.count ??
          inner.totalCount ??
          body.total ??
          inner[key].length,
      };
    }
  }
  return { rows: [], total: 0 };
}

function normalizeApiErrorMessage(err) {
  const d = err?.response?.data;
  if (d == null) return err?.message || '';
  if (typeof d.data === 'string' && d.data.trim()) return d.data.trim();
  if (typeof d.message === 'string' && d.message) return d.message;
  if (typeof d.error === 'string' && d.error) return d.error;
  return err?.message || 'Request failed';
}

function formatListDate(val) {
  if (val == null || val === '') return '—';
  try {
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return String(val).slice(0, 10);
    return d.toISOString().slice(0, 10);
  } catch {
    return String(val).slice(0, 10);
  }
}

export function parseRefStoFromRemarks(remarks) {
  const m = String(remarks ?? '').match(/Ref\s+STO:\s*([^|]+)/i);
  return m ? m[1].trim() : '';
}

/** Unwrap `{ success, data }` or nested list envelopes to the STI document. */
export function unwrapStockTransferInDocument(payload) {
  let v = payload;
  for (let depth = 0; depth < 4; depth += 1) {
    if (!v || typeof v !== 'object' || Array.isArray(v)) break;
    const hasDocShape =
      v.transferNumber != null ||
      v.transferInNo != null ||
      v.transferInNumber != null ||
      Array.isArray(v.items);
    if (hasDocShape) break;
    if (v.success === true && v.data != null && typeof v.data === 'object') {
      v = v.data;
      continue;
    }
    if (v.data != null && typeof v.data === 'object' && !Array.isArray(v.data)) {
      const inner = v.data;
      if (
        inner.transferNumber != null ||
        inner._id != null ||
        Array.isArray(inner.items)
      ) {
        v = inner;
        continue;
      }
    }
    break;
  }
  return v;
}

function formatShipmentDisplayValue(val) {
  if (val == null || val === '') return '';
  if (typeof val === 'number' && Number.isNaN(val)) return '';
  return String(val);
}

/** Read shipment fields from API / Mongo (camelCase and legacy aliases). */
export function pickShipmentFields(doc) {
  if (!doc || typeof doc !== 'object') {
    return {
      productWeightGrams: '',
      transporterName: '',
      noOfBoxes: '',
      grossWeight: '',
    };
  }
  return {
    productWeightGrams: formatShipmentDisplayValue(
      doc.productWeightGrams ?? doc.product_weight_grams ?? doc.productWeight
    ),
    transporterName: formatShipmentDisplayValue(
      doc.transporterName ?? doc.transporter_name ?? doc.transporter ?? doc.courierName
    ),
    noOfBoxes: formatShipmentDisplayValue(
      doc.noOfBoxes ?? doc.no_of_boxes ?? doc.numberOfBoxes ?? doc.boxes
    ),
    grossWeight: formatShipmentDisplayValue(
      doc.grossWeight ?? doc.gross_weight ?? doc.totalGrossWeight
    ),
  };
}

export function isShipmentEmpty(shipment) {
  if (!shipment) return true;
  return (
    !String(shipment.productWeightGrams ?? '').trim() &&
    !String(shipment.transporterName ?? '').trim() &&
    !String(shipment.noOfBoxes ?? '').trim() &&
    !String(shipment.grossWeight ?? '').trim()
  );
}

/** Resolve Mongo / string id from various API shapes. */
export function extractStockTransferInId(r) {
  if (!r || typeof r !== 'object') return '';
  const candidates = [r._id, r.id, r.stockTransferInId, r.stiId, r.mongoId, r.transferInId];
  for (const c of candidates) {
    if (c == null) continue;
    if (typeof c === 'string' && c.trim()) return c.trim();
    if (typeof c === 'object' && c.$oid) return String(c.$oid);
    if (typeof c === 'object' && typeof c.toString === 'function') {
      const s = String(c.toString());
      if (s && s !== '[object Object]' && /^[a-f\d]{24}$/i.test(s)) return s;
    }
  }
  return '';
}

/** Map API document to Stock Transfer In list row shape (aligned with STO list). */
export function mapStockTransferInListRow(r, index = 0) {
  if (!r || typeof r !== 'object') return null;
  const mongoId = extractStockTransferInId(r);
  const transferNo = String(
    r.transferNumber ?? r.transferInNo ?? r.transferInNumber ?? ''
  ).trim();
  const id = mongoId || `sti-row-${index}`;
  const expected =
    r.expectedDeliveryDate ?? r.expected_delivery_date ?? r.receivedDate ?? '';
  const remarks = r.remarks ?? '';
  const referenceSto =
    r.referenceSto ??
    r.referenceStockTransferOutNo ??
    parseRefStoFromRemarks(remarks);
  return {
    id: String(id),
    _id: mongoId || String(id),
    transferInNo: transferNo || '—',
    referenceSto: referenceSto || '—',
    sourceLocation: r.sourceLocation ?? r.sourceFacilityId ?? '—',
    receivingLocation:
      r.destinationLocation ?? r.receivingLocation ?? r.destinationFacilityId ?? '—',
    status: r.status ?? '—',
    totalQty: r.totalTransferInQty ?? r.totalQty ?? 0,
    totalAmount: r.totalTransferInAmount ?? r.totalAmount ?? 0,
    receivedDate: formatListDate(expected),
    createdAt: formatListDate(r.createdAt ?? r.created_at),
  };
}

/**
 * Build POST body matching partner-portal-be `stockTransferInValidator` (no extra keys).
 * UI line "received qty" maps to API `orderedQty`.
 */
export function buildStockTransferInPayload(form) {
  const {
    sourceLocation,
    sourceLocationId,
    sourceAddress,
    destinationLocation,
    destinationLocationId,
    destinationAddress,
    totalTransferInQty,
    totalTransferInAmount,
    remarks,
    referenceStoNo,
    receivedDate,
    stoExpectedDeliveryIso,
    productWeightGrams,
    transporterName,
    noOfBoxes,
    grossWeight,
    items,
    status,
  } = form;

  const lines = (Array.isArray(items) ? items : []).map((item, idx) => ({
    lineNo: item.lineNo != null ? Number(item.lineNo) : idx + 1,
    skuId: String(item.skuId || '').trim(),
    description: String(item.description ?? '').trim(),
    pack: String(item.pack ?? '').trim(),
    uom: String(item.uom ?? '').trim(),
    availableQty: item.availableQty === '' || item.availableQty == null ? 0 : Number(item.availableQty) || 0,
    orderedQty: Number(item.receivedQty ?? item.orderedQty) || 0,
    unitPrice: Number(item.unitPrice) || 0,
    tax: Number(item.tax) || 0,
    qcRequired: item.qcRequired ?? 'No',
    itemCube: item.itemCube === '' || item.itemCube == null ? null : Number(item.itemCube),
    itemWeight: item.itemWeight === '' || item.itemWeight == null ? null : Number(item.itemWeight),
    totalAmount: Number(item.totalAmount) || 0,
  }));

  let remarksOut = String(remarks ?? '').trim();
  const ref = String(referenceStoNo ?? '').trim();
  if (ref && !remarksOut.includes(ref)) {
    remarksOut = remarksOut ? `${remarksOut} | Ref STO: ${ref}` : `Ref STO: ${ref}`;
  }

  const expectedDeliveryDate =
    receivedDate || stoExpectedDeliveryIso || undefined;

  const payload = {
    sourceLocation: String(sourceLocation || '').trim(),
    destinationLocation: String(destinationLocation || '').trim(),
    sourceAddress: String(sourceAddress ?? ''),
    destinationAddress: String(destinationAddress ?? ''),
    totalTransferInQty: Number(totalTransferInQty) || 0,
    totalTransferInAmount: Number(totalTransferInAmount) || 0,
    remarks: remarksOut,
    expectedDeliveryDate,
    productWeightGrams: Number(productWeightGrams) || 0,
    transporterName: String(transporterName ?? ''),
    noOfBoxes: Number(noOfBoxes) || 0,
    grossWeight: Number(grossWeight) || 0,
    items: lines,
    status: status || 'CREATED',
  };

  if (sourceLocationId) payload.sourceLocationId = sourceLocationId;
  if (destinationLocationId) payload.destinationLocationId = destinationLocationId;

  return payload;
}

/**
 * POST /api/stock-transfer-in
 */
export async function createStockTransferIn(payload) {
  try {
    const response = await api.post('stock-transfer-in', payload);
    if (response.data?.success === false) {
      throw new Error(response.data?.message || 'Failed to create stock transfer in');
    }
    return response.data?.data ?? response.data;
  } catch (err) {
    throw new Error(normalizeApiErrorMessage(err) || 'Failed to create stock transfer in');
  }
}

/**
 * GET /api/stock-transfer-in?page=&limit=&status=
 * Pass `destinationLocationId` (facility mongo id) like STO uses source filter via header.
 */
export async function fetchStockTransferInList({
  page = 1,
  limit = 10,
  status = 'all',
  destinationLocationId,
} = {}) {
  try {
    const params = { page, limit, status };
    if (destinationLocationId) {
      params.destinationLocationId = destinationLocationId;
    }
    const response = await api.get('stock-transfer-in', { params });
    if (response.data?.success === false) {
      throw new Error(response.data?.message || 'Failed to fetch stock transfer in list');
    }
    const { rows, total } = normalizeListResponse(response.data);
    const mapped = rows.map((row, index) => mapStockTransferInListRow(row, index)).filter(Boolean);
    return {
      rows: mapped,
      total: typeof total === 'number' && total >= mapped.length ? total : mapped.length,
    };
  } catch (err) {
    throw new Error(normalizeApiErrorMessage(err) || 'Failed to fetch stock transfer in list');
  }
}

/**
 * GET /api/stock-transfer-in/:mongoId
 */
export async function getStockTransferInById(mongoId) {
  if (!mongoId) throw new Error('Missing stock transfer in id');
  try {
    const response = await api.get(`stock-transfer-in/${encodeURIComponent(String(mongoId))}`);
    if (response.data?.success === false) {
      throw new Error(response.data?.message || 'Failed to load stock transfer in');
    }
    const raw = response.data?.data ?? response.data;
    return unwrapStockTransferInDocument(raw);
  } catch (err) {
    throw new Error(normalizeApiErrorMessage(err) || 'Failed to load stock transfer in');
  }
}
